import { useQueryClient } from '@tanstack/react-query';
import { useAxiosInfiniteQuery, useAxiosMutation, useAxiosQuery } from './use-axios.hook';
import { UpdateCivil3dPropertySetApiRequest } from '../../../../types/requests/civil3d-property-sets/update-civil3d-property-set.request';
import {
  createCivil3dPropertySet,
  deleteCivil3dPropertySet,
  getCivil3dPropertySet,
  searchCivil3dPropertySets,
  updateCivil3dPropertySet,
} from '../../api/civil3d-property-sets.api';
import { CreateCivil3dPropertySetApiRequest } from '../../../../types/requests/civil3d-property-sets/create-civil3d-property-set.request';
import { Civil3dPropertySetApiModel } from '../../../../types/models/civil3d-property-set.model';
import { GetCivil3dPropertySetApiRequest } from '../../../../types/requests/civil3d-property-sets/get-civil3d-property-set.request';
import { DeleteCivil3dPropertySetApiRequest } from '../../../../types/requests/civil3d-property-sets/delete-civil3d-property-set.request';
import { SearchCivil3dPropertySetsApiRequest } from '../../../../types/requests/civil3d-property-sets/search-civil3d-property-sets.request';

const baseQueryKey = 'civil3d-property-set';
const searchQueryKey = 'search';

export const useUpdateCivil3dPropertySet = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: UpdateCivil3dPropertySetApiRequest) => (await updateCivil3dPropertySet(params)).data,
    onSuccess: (civil3dPropertySet) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey &&
          (queryKey[1] === 'search' ||
            (queryKey[1] as UpdateCivil3dPropertySetApiRequest)?.civil3dPropertySetId === civil3dPropertySet?.id),
      }),
  });
  return [request, { ...result, civil3dPropertySet: data }] as const;
};

export const useCreateCivil3dPropertySet = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: CreateCivil3dPropertySetApiRequest) => (await createCivil3dPropertySet(params)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseQueryKey, searchQueryKey] }),
  });
  return [request, { ...result, civil3dPropertySet: data }] as const;
};

export const useGetCivil3dPropertySet = <T extends Civil3dPropertySetApiModel>(
  params: GetCivil3dPropertySetApiRequest,
) => {
  const { data, ...result } = useAxiosQuery<T>({
    queryKey: [baseQueryKey, params],
    queryFn: async () => (await getCivil3dPropertySet<T>(params)).data,
    enabled:
      Boolean(params.templateId && !Number.isNaN(params.templateId)) &&
      Boolean(params.civil3dPropertySetId && !Number.isNaN(params.civil3dPropertySetId)),
  });
  return { ...result, civil3dPropertySet: data } as const;
};

export const useSearchCivil3dPropertySets = <T extends Civil3dPropertySetApiModel>(
  params: SearchCivil3dPropertySetsApiRequest,
) => {
  const { data, ...result } = useAxiosInfiniteQuery({
    queryKey: [baseQueryKey, searchQueryKey, params],
    queryFn: async ({ pageParam }) => (await searchCivil3dPropertySets<T>({ cursor: pageParam, ...params })).data,
    initialPageParam: params.cursor,
    getNextPageParam: (lastResponse) => lastResponse.nextCursor,
    enabled: Boolean(params.templateId && !Number.isNaN(params.templateId)),
  });
  return { ...result, response: data } as const;
};

export const useDeleteCivil3dPropertySets = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DeleteCivil3dPropertySetApiRequest) => (await deleteCivil3dPropertySet(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, result] as const;
};
